import clsx from "clsx";
import type { BridgeStatusDto, FloorStatusDto, HealthStatus } from "../types/api";
import { relativeTime } from "../lib/relativeTime";
import { fmtTime } from "../lib/historyFormat";
import { useNow } from "../hooks/useNow";
import { healthStyle } from "../lib/statusStyles";
import { useVideoStatus } from "../hooks/useVideoStatus";
import { videoProblemCount } from "../lib/videoStatus";
import StatusBadge from "./StatusBadge";
import Tip from "./Tip";
import ZoneList from "./ZoneList";

interface BridgeRowProps {
  bridge: BridgeStatusDto;
  /**
   * Server-side "now" for stable relative times across the page. After first
   * paint the 1-second tick takes over so the label keeps moving.
   */
  referenceNow?: string | null;
}

/**
 * Single bridge (= parking lot) row inside a SiteCard.
 *
 *   ┌──────────────────────────────────────────────────────────────────────┐
 *   │ 더에이치 타워 주차장              [데이터 정체]      13시간 전        │
 *   │ PKL2510010000001                                                      │
 *   │ ●지하2층  ●지하1층  ●1층  ●2층  ●3층  ●4층                          │
 *   └──────────────────────────────────────────────────────────────────────┘
 *
 * Design choice — flat row with inline floor chips instead of nested
 * `<details>`:
 *
 *   - "부분 장애" sections used to auto-expand every DOWN bridge so the user
 *     could see which floor was at fault. With 10+ DOWN lots this produced
 *     a wall of expanded children and forced heavy scrolling.
 *   - A horizontal chip row keeps the per-floor status *always* visible —
 *     across every status group (정상/정체/미운영/종료) — without any click,
 *     and uses ~1 line of vertical space regardless of floor count.
 *   - Color dot + floor name is enough at a glance; hover/focus tooltip
 *     surfaces the exact "마지막 갱신 N시간 전" for forensic detail.
 *   - DOWN/UNKNOWN floors get a red ring + bold label so the offending floor
 *     reads first even in a wide chip row.
 */
export default function BridgeRow({
  bridge,
  referenceNow,
}: BridgeRowProps) {
  // Re-render every second so the relative-time labels keep ticking between
  // TanStack Query refetches.
  const now = useNow();

  // 영상분석 대상은 BE 가 BridgeStatusDto.hasVideoAnalysis 로 알려준다 (캐시에 lot 등장 여부).
  // 플래그가 false 면 query 가 비활성 → 요청 자체가 안 나가서 404 폭탄/로그 노이즈 없음.
  const { data: videoStatus } = useVideoStatus(bridge.lotId, bridge.hasVideoAnalysis);
  const isVideoLot = !!videoStatus;

  // 영상분석 mismatch 는 이력 적재용 보조 신호라 lot 의 주 상태를 바꾸지 않는다.
  // lot 배지/분류는 브릿지 DB 헬스(bridge.status) 그대로 두고, 영상 문제 구역은
  // 약한 주황(degraded) 경고로만 가볍게 표시한다.
  const videoProblems = videoProblemCount(videoStatus);
  // 현장 GPU 무신호(연결 끊김) — stale 면 zones 의 OK/PROBLEM 은 낡은 값이라 '연결 끊김'으로 표기.
  const videoStale = !!videoStatus?.stale;

  const isDown = bridge.status === "DOWN";
  const isGray = bridge.status === "STALE" || bridge.status === "RETIRED";

  // Fall back to lotId if the backend ever sends an empty lotName.
  const trimmedName = bridge.lotName?.trim();
  const primaryLabel = trimmedName ? trimmedName : bridge.lotId;
  const showSecondaryId = !!trimmedName;

  // Anchor for relative-time math.
  const referenceMs = referenceNow ? new Date(referenceNow).getTime() : now;
  const tickAnchor = !Number.isNaN(referenceMs)
    ? Math.max(referenceMs, now)
    : now;

  const relativeLabel = relativeTime(bridge.lastUpdatedAt, {
    referenceNow: tickAnchor,
  });

  const floors = bridge.floors ?? [];

  return (
    <li
      className={clsx(
        "px-4 py-3 sm:px-5",
        "border-t border-surface-border dark:border-surface-darkBorder",
        "first:border-t-0",
      )}
    >
      <div className="grid grid-cols-[1fr_auto_auto] items-center gap-3 sm:gap-4">
        <div className="flex min-w-0 flex-col">
          <span
            className="truncate text-sm font-medium text-slate-800 dark:text-slate-100 sm:text-[15px]"
            title={primaryLabel}
          >
            {primaryLabel}
            {isVideoLot ? (
              <span className="ml-2 align-middle text-[11px] font-normal text-slate-400 dark:text-slate-500">
                {videoStatus.zones.length}개 구역
              </span>
            ) : (
              floors.length > 0 && (
                <span className="ml-2 align-middle text-[11px] font-normal text-slate-400 dark:text-slate-500">
                  {floors.length}개 층
                </span>
              )
            )}
          </span>
          {showSecondaryId && (
            <span
              className="truncate font-mono text-xs text-slate-400 dark:text-slate-500"
              title={bridge.lotId}
            >
              {bridge.lotId}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* 관리자가 감시에서 뺀 lot 은 서버가 상태를 RETIRED 로 고정한다.
              "운영 종료(사이트 DB 마킹)" 와 구분되도록 라벨만 바꿔 준다. */}
          <StatusBadge
            status={bridge.status}
            kind="health"
            size="sm"
            label={bridge.monitored ? undefined : "감시 제외"}
          />
          {videoStale ? (
            <span
              className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400"
              title={`현장 GPU 영상분석 무신호(연결 끊김) — 마지막 수신 ${relativeTime(videoStatus?.lastReceivedAt ?? null, { referenceNow: tickAnchor })}`}
            >
              영상 연결 끊김
            </span>
          ) : (
            videoProblems > 0 && (
              <span
                className="inline-flex items-center gap-1 rounded-full border border-status-degraded/30 bg-status-degraded-bg px-2 py-0.5 text-[11px] font-medium text-status-degraded-fg dark:border-status-degraded-dark/30 dark:bg-status-degraded-darkBg dark:text-status-degraded-darkFg"
                title={`영상분석에서 ${videoProblems}개 구역 불일치 감지 (이력 기록용 약한 경고)`}
              >
                영상 {videoProblems}구역 주의
              </span>
            )
          )}
        </div>

        <span
          className={clsx(
            "text-xs tabular-nums sm:text-sm",
            !isGray && isDown
              ? "font-semibold text-status-majorOutage-fg dark:text-status-majorOutage-darkFg"
              : isGray
                ? "text-slate-400 dark:text-slate-500"
                : "text-slate-500 dark:text-slate-400",
          )}
          title={bridge.lastUpdatedAt ?? undefined}
        >
          {relativeLabel}
        </span>
      </div>

      {/*
        영상분석 lot 은 층(floor) 대신 구역(zone) 목록을 보여준다.
        그 외 lot 은 기존 층 칩 행을 그대로 유지한다.
      */}
      {isVideoLot ? (
        <ZoneList
          zones={videoStatus.zones}
          referenceNow={videoStatus.generatedAt}
          stale={videoStale}
        />
      ) : (
        floors.length > 0 && (
          <ul
            // 모바일 간격을 넓히는 건 미관이 아니라 칩의 확장된 히트 영역(위아래 6px)이
            // 이웃 줄과 겹치지 않게 하기 위해서다.
            className="mt-2 flex flex-wrap gap-3 sm:gap-1.5"
            aria-label={`${primaryLabel} 층 상태`}
          >
            {floors.map((floor, idx) => (
              <FloorChip
                key={floor.floorSeq ?? `${bridge.lotId}-floor-${idx}`}
                floor={floor}
                tickAnchor={tickAnchor}
              />
            ))}
          </ul>
        )
      )}
    </li>
  );
}

interface FloorChipProps {
  floor: FloorStatusDto;
  tickAnchor: number;
}

/**
 * Single floor chip — color dot + short floor name. Hovering (or tabbing to)
 * the chip reveals when that floor last ingested data.
 *
 * Why a custom popover instead of the native `title` attribute: the native
 * tooltip only appears after a ~1s delay, can't be styled, and never shows on
 * keyboard focus. The uptime bars in `/history` and `/camera` already use this
 * same `group-hover:block` popover, so floors now behave consistently.
 *
 * Tooltip content — absolute timestamp *and* relative age. The absolute value
 * answers "언제 끊겼나" (needed when cross-checking the site DB), the relative
 * one answers "얼마나 됐나" at a glance.
 *
 * Urgency emphasis:
 *   DOWN/UNKNOWN  → red ring + red text, bold
 *   STALE/RETIRED → muted gray, no emphasis
 *   UP            → subtle slate, no emphasis
 */
function FloorChip({ floor, tickAnchor }: FloorChipProps) {
  const style = healthStyle(floor.status);
  const isAlert = isAlertStatus(floor.status);
  const isMuted = floor.status === "STALE" || floor.status === "RETIRED";

  const trimmedName = floor.floorName?.trim();
  const label =
    trimmedName ||
    floor.floorSeq ||
    "?";

  const relative = relativeTime(floor.lastUpdatedAt, {
    referenceNow: tickAnchor,
  });
  const headline = `${label} · ${style.label}`;
  // RETIRED/UNKNOWN 층은 lastUpdatedAt 이 null 일 수 있다 — 이때 "—" 만 띄우면
  // "0분 전" 처럼 오해할 여지가 있어 명시적으로 기록 없음이라고 쓴다.
  const detail = floor.lastUpdatedAt
    ? `마지막 업데이트 ${fmtTime(floor.lastUpdatedAt)} · ${relative}`
    : "마지막 업데이트 기록 없음";

  return (
    <li className="flex">
      {/*
        패널은 칩 위쪽으로 띄운다 — 칩 행은 각 주차장 행의 맨 아래라, 위로 열면 같은 카드
        안(주차장 이름 줄 위)에 머물러 SiteCard 의 overflow-hidden 에 잘리지 않는다.
        아래로 열면 카드 마지막 행에서 잘린다.
      */}
      <Tip
        ariaLabel={`${headline} · ${detail}`}
        placement="top"
        align="start"
        panelClassName="whitespace-nowrap"
        content={
          <>
            <span className="block font-semibold">{headline}</span>
            <span className="block tabular-nums text-slate-200">{detail}</span>
          </>
        }
        className={clsx(
          "relative inline-flex items-center gap-1.5 rounded-full border px-2",
          "text-[11px] leading-none transition-colors",
          // 모바일에서는 칩 자체를 조금 키우고(32px) 히트 영역을 위아래로 6px 더 넓혀
          // 44px 를 확보한다. 행 간격(gap-3=12px)과 정확히 맞물려 이웃 칩과 겹치지 않는다.
          "min-h-[2rem] py-1 after:absolute after:-inset-y-1.5 after:inset-x-0 after:content-['']",
          "sm:min-h-0 sm:py-0.5 sm:after:hidden",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-slate-400",
          isAlert
            ? clsx(
                "font-semibold",
                style.bg,
                style.fg,
                style.border,
                "ring-1 ring-status-majorOutage/40 dark:ring-status-majorOutage-dark/40",
              )
            : isMuted
              ? "border-slate-200 bg-slate-50 text-slate-400 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-500"
              : clsx(
                  "border-surface-border bg-white text-slate-600",
                  "dark:border-surface-darkBorder dark:bg-slate-900/40 dark:text-slate-300",
                ),
        )}
      >
        <span
          aria-hidden="true"
          className={clsx("inline-block h-1.5 w-1.5 rounded-full", style.solid)}
        />
        <span className="truncate max-w-[7rem]">{label}</span>
      </Tip>
    </li>
  );
}

function isAlertStatus(s: HealthStatus): boolean {
  return s === "DOWN" || s === "UNKNOWN";
}
